import { Add, Delete } from "@mui/icons-material";
import { Alert, Box, Button, IconButton, MenuItem, Stack, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import DataPicker from "../DataPicker";
import { FlussValue, ScopeOption } from "../fluss-builder-types";
import { translateMetadataLabel } from "modules/record/shared/services/recordMetadataLabels";
import { OPERATORS_BY_TYPE } from "modules/record/record-producer/components/record-producer-fields/condition-field/utils/operators.config";
import { TemporalConditionValueField } from "modules/record/record-producer/components/record-producer-fields/condition-field/TemporalConditionValueField";
import { InValueField } from "modules/record/record-producer/components/record-producer-fields/condition-field/InValueField";
import { columnForMeta } from "../FlussConditionBuilder";

type FindRecordCondition = {
    type: "condition";
    field: string;
    operator: string;
    value: FlussValue | { amount: number; unit: string } | unknown[] | undefined;
};

type FindRecordFilter = {
    type: "logical";
    operator: "and" | "or";
    conditions: FindRecordCondition[];
};

const OPERATOR_LABELS: Record<string, [string, string]> = {
    eq: ["equal", "é igual a"], neq: ["not_equal", "é diferente de"],
    in: ["in", "está em"], contains: ["contains", "contém"],
    stw: ["starts_with", "começa com"], edw: ["ends_with", "termina com"],
    not_contains: ["not_contains", "não contém"], on: ["on", "na data"], not_on: ["not_on", "fora da data"],
    lt: ["lt", "anterior a"], lte: ["lte", "anterior ou igual"], gt: ["gt", "maior que"],
    gte: ["gte", "maior ou igual"], between: ["between", "entre"],
    relative_last: ["relative_last", "nos últimos"], relative_next: ["relative_next", "nos próximos"],
    empty: ["empty", "vazio"], not_empty: ["not_empty", "não vazio"],
};
const operatorsForType = (type?: string) => (OPERATORS_BY_TYPE[type || "TEXT"] || OPERATORS_BY_TYPE.TEXT)
    .filter((operator) => !operator.isDynamic && !operator.value.endsWith("_dynamic")
        && !operator.value.includes("_chain"));

const normalize = (value: unknown): FindRecordFilter => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        return { type: "logical", operator: "and", conditions: [] };
    }
    const raw = value as Partial<FindRecordFilter>;
    return {
        type: "logical",
        operator: raw.operator === "or" ? "or" : "and",
        conditions: Array.isArray(raw.conditions)
            ? raw.conditions.filter((item): item is FindRecordCondition => item?.type === "condition")
            : [],
    };
};

const emptyCondition = (field = ""): FindRecordCondition => ({
    type: "condition",
    field,
    operator: "eq",
    value: undefined,
});

export const findRecordFieldLabel = (t: TFunction, field: ScopeOption): string => {
    const translated = translateMetadataLabel(t, field);
    return translated === field.label && /^[a-z][a-z0-9_]*(\.[a-z0-9_-]+)+$/i.test(field.label)
        ? field.name : translated || field.name;
};

export const FindRecordFilterField = ({
    value,
    onChange,
    fields,
    disabled,
}: {
    value: unknown;
    onChange: (value: FindRecordFilter) => void;
    fields: ScopeOption[];
    disabled?: boolean;
}) => {
    const { t } = useTranslation();
    const filter = normalize(value);
    const commit = (conditions: FindRecordCondition[], operator = filter.operator) => onChange({ type: "logical", operator, conditions });
    const update = (index: number, patch: Partial<FindRecordCondition>) => {
        commit(filter.conditions.map((row, rowIndex) => rowIndex === index ? { ...row, ...patch } : row));
    };

    return (
        <Stack spacing={1}>
            {filter.conditions.length === 0 && (
                <Alert severity="info">
                    {t("fluss.node_panel.service.param.filter.add_condition_hint", { defaultValue: "Adicione pelo menos uma condição completa antes de publicar Buscar registro." })}
                </Alert>
            )}
            <TextField
                select
                size="small"
                value={filter.operator}
                onChange={(event) => commit(filter.conditions, event.target.value === "or" ? "or" : "and")}
                disabled={disabled}
                inputProps={{ "aria-label": String(t("fluss.node_panel.service.param.filter.operator", { defaultValue: "Filter operator" })) }}
            >
                <MenuItem value="and">{t("fluss.node_panel.service.param.filter.and", { defaultValue: "All conditions" })}</MenuItem>
                <MenuItem value="or">{t("fluss.node_panel.service.param.filter.or", { defaultValue: "Any condition" })}</MenuItem>
            </TextField>
            {filter.conditions.map((condition, index) => {
                const selectedField = fields.find((field) => field.name === condition.field);
                return (
                    <Stack key={index} spacing={1} sx={{ minWidth: 0 }}>
                        <TextField select fullWidth size="small" label={t("fluss.node_panel.service.param.filter.field", { defaultValue: "Campo" })} value={condition.field} onChange={(event) => update(index, { field: event.target.value, operator: "eq", value: undefined })} disabled={disabled}>
                            {fields.map((field) => <MenuItem key={field.name} value={field.name}>{findRecordFieldLabel(t, field)}</MenuItem>)}
                        </TextField>
                        <TextField select fullWidth size="small" label={t("fluss.node_panel.service.param.filter.comparison", { defaultValue: "Operador" })} value={condition.operator} onChange={(event) => {
                            const operator = event.target.value;
                            update(index, { operator, value: ["relative_last", "relative_next"].includes(operator)
                                ? { amount: 1, unit: selectedField?.type === "DATE" ? "day" : "minute" } : undefined });
                        }} disabled={disabled}>
                            {operatorsForType(selectedField?.type).map(({ value: operator }) => <MenuItem key={operator} value={operator}>{t(`fluss.node_panel.service.param.filter.comparison_operator.${OPERATOR_LABELS[operator]?.[0] || operator}`, { defaultValue: OPERATOR_LABELS[operator]?.[1] || operator })}</MenuItem>)}
                        </TextField>
                        {!(["empty", "not_empty"].includes(condition.operator)) && <Box sx={{ width: "100%", minWidth: 0 }}>
                            <Box component="label" sx={{ display: "block", fontSize: 12, color: "text.secondary", mb: 0.5 }}>{t("fluss.node_panel.service.param.filter.value_label", { defaultValue: "Valor" })}</Box>
                            {condition.operator === "in" && selectedField ? (
                                <InValueField column={selectedField.column || columnForMeta({
                                    path: selectedField.name, label: selectedField.label, type: selectedField.type,
                                    columnId: selectedField.id, referencedTable: selectedField.referencedTableName,
                                }, index)} value={condition.value} onChange={(next) => update(index, { value: next })} disabled={disabled} />
                            ) : condition.operator === "between" && selectedField && ["INTEGER", "DECIMAL", "FLOAT", "CURRENCY"].includes(selectedField.type) ? (
                                <Stack direction="row" spacing={1}>
                                    {[0, 1].map((bound) => <DataPicker key={bound}
                                        value={Array.isArray(condition.value) ? condition.value[bound] as FlussValue : undefined}
                                        onChange={(next) => { const values = Array.isArray(condition.value) ? [...condition.value] : [undefined, undefined]; values[bound] = next; update(index, { value: values }); }}
                                        expectedType={selectedField.type} destinationField={selectedField} disabled={disabled}
                                        ariaLabel={String(t(`fluss.temporal.bound.${bound}`, { defaultValue: bound ? "Até" : "De" }))} />)}
                                </Stack>
                            ) : selectedField && ["DATE", "DATETIME"].includes(selectedField.type) && ["relative_last", "relative_next"].includes(condition.operator) ? (
                                <TemporalConditionValueField columnType={selectedField.type} operator={condition.operator}
                                    value={condition.value} onChange={(next) => update(index, { value: next })} disabled={disabled} />
                            ) : condition.operator === "between" && selectedField && ["DATE", "DATETIME"].includes(selectedField.type) ? (
                                <TemporalConditionValueField columnType={selectedField.type} operator="between"
                                    value={condition.value} onChange={(next) => update(index, { value: next })} disabled={disabled} />
                            ) : <DataPicker
                                value={Array.isArray(condition.value) || (condition.value && typeof condition.value === "object") ? undefined : condition.value as FlussValue | undefined}
                                onChange={(next) => update(index, { value: next })}
                                expectedType={selectedField?.type === "DATETIME" && ["on", "not_on"].includes(condition.operator) ? "DATE" : selectedField?.type}
                                destinationField={selectedField}
                                disabled={disabled}
                                ariaLabel={String(t("fluss.node_panel.service.param.filter.value", { defaultValue: "Filter value" }))}
                            />}
                        </Box>}
                        <IconButton size="small" sx={{ alignSelf: "flex-end" }} aria-label={String(t("fluss.node_panel.service.param.filter.remove", { defaultValue: "Remover condição" }))} disabled={disabled} onClick={() => commit(filter.conditions.filter((_, rowIndex) => rowIndex !== index))}>
                            <Delete fontSize="small" />
                        </IconButton>
                    </Stack>
                );
            })}
            <Button size="small" startIcon={<Add />} disabled={disabled || fields.length === 0} onClick={() => commit([...filter.conditions, emptyCondition(fields[0]?.name || "")])}>
                {t("fluss.node_panel.service.param.filter.add", { defaultValue: "Condition" })}
            </Button>
        </Stack>
    );
};

export default FindRecordFilterField;
